import { useEffect, useRef } from 'react';
import { Alert, Box, Skeleton } from '@mui/material';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import { GOOGLE_MAP_ID, addMapPin, clearMapOverlay, type MapOverlay } from '../../utils/googleMapPins';
import { isValidMapCenter, useMapCenter } from '../../utils/mapCenter';

export interface HeatMapPoint {
  lat: number;
  lng: number;
  weight: number;
}

interface GoogleMapPanelProps {
  center?: { lat: number; lng: number };
  zoom?: number;
  height?: number | string | { xs?: number | string; sm?: number | string; md?: number | string };
  markers?: { lat: number; lng: number; title?: string; color?: string }[];
  route?: { lat: number; lng: number }[];
  heatMapPoints?: HeatMapPoint[];
  showHeatMap?: boolean;
  showTraffic?: boolean;
  onMapClick?: (lat: number, lng: number) => void;
}

type MapLike = {
  setCenter: (c: unknown) => void;
  setZoom: (z: number) => void;
  fitBounds: (b: unknown, o?: unknown) => void;
};

type GMaps = {
  Map: new (...args: unknown[]) => MapLike;
  Circle: new (...args: unknown[]) => MapOverlay;
  Polyline: new (...args: unknown[]) => MapOverlay;
  LatLngBounds: new () => { extend: (p: unknown) => void };
  TrafficLayer: new () => MapOverlay;
  event: {
    addListener: (t: unknown, e: string, fn: (ev: { latLng?: { lat: () => number; lng: () => number } }) => void) => unknown;
    removeListener: (l: unknown) => void;
    clearInstanceListeners: (t: unknown) => void;
  };
};

/**
 * HeatmapLayer was removed from Maps JS API v3.65+.
 * Approximate demand density with weighted Circles so the dispatcher map stays usable.
 */
export default function GoogleMapPanel({
  center,
  zoom = 12,
  height = 320,
  markers = [],
  route = [],
  heatMapPoints = [],
  showHeatMap = false,
  showTraffic = false,
  onMapClick,
}: GoogleMapPanelProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<MapLike | null>(null);
  const heatOverlaysRef = useRef<MapOverlay[]>([]);
  const markerInstancesRef = useRef<MapOverlay[]>([]);
  const routeRef = useRef<MapOverlay | null>(null);
  const trafficLayerRef = useRef<MapOverlay | null>(null);
  const clickListenerRef = useRef<unknown>(null);
  const { ready, error, hasKey } = useGoogleMaps();
  const preferred = isValidMapCenter(center)
    ? center
    : (markers[0] || heatMapPoints[0] || route[0] || undefined);
  const { center: resolvedCenter, ready: centerReady } = useMapCenter(preferred);

  useEffect(() => {
    if (!ready || !centerReady || !resolvedCenter || !mapRef.current) return;

    const g = (window as unknown as { google: { maps: GMaps } }).google;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new g.maps.Map(mapRef.current, {
        center: resolvedCenter,
        zoom,
        mapId: GOOGLE_MAP_ID,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        gestureHandling: 'greedy',
      });
    } else if (isValidMapCenter(center)) {
      mapInstanceRef.current.setCenter(center);
      mapInstanceRef.current.setZoom(zoom);
    }

    const map = mapInstanceRef.current;

    if (showTraffic) {
      if (!trafficLayerRef.current) {
        trafficLayerRef.current = new g.maps.TrafficLayer();
      }
      trafficLayerRef.current.setMap?.(map);
    } else if (trafficLayerRef.current) {
      trafficLayerRef.current.setMap?.(null);
    }

    markerInstancesRef.current.forEach((m) => clearMapOverlay(m));
    markerInstancesRef.current = [];

    const bounds = new g.maps.LatLngBounds();
    markers.forEach((marker) => {
      const pos = { lat: marker.lat, lng: marker.lng };
      markerInstancesRef.current.push(addMapPin(map, {
        lat: marker.lat,
        lng: marker.lng,
        title: marker.title,
        color: marker.color,
      }));
      bounds.extend(pos);
    });

    if (routeRef.current) {
      clearMapOverlay(routeRef.current);
      routeRef.current = null;
    }
    if (route.length > 1) {
      routeRef.current = new g.maps.Polyline({
        map,
        path: route,
        strokeColor: '#2563eb',
        strokeOpacity: 0.8,
        strokeWeight: 4,
      });
      route.forEach((p) => bounds.extend(p));
    }

    heatOverlaysRef.current.forEach((layer) => clearMapOverlay(layer));
    heatOverlaysRef.current = [];

    if (showHeatMap && heatMapPoints.length > 0) {
      const maxWeight = Math.max(...heatMapPoints.map((p) => p.weight), 1);
      heatMapPoints.forEach((p) => {
        const intensity = Math.min(1, p.weight / maxWeight);
        const circle = new g.maps.Circle({
          map,
          center: { lat: p.lat, lng: p.lng },
          radius: 60 + intensity * 220,
          fillColor: intensity > 0.66 ? '#dc2626' : intensity > 0.33 ? '#f97316' : '#fbbf24',
          fillOpacity: 0.18 + intensity * 0.42,
          strokeWeight: 0,
          clickable: false,
        });
        heatOverlaysRef.current.push(circle);
        bounds.extend({ lat: p.lat, lng: p.lng });
      });
    }

    if (markers.length > 1 || route.length > 1 || (showHeatMap && heatMapPoints.length > 1)) {
      map.fitBounds(bounds, 48);
    }

    if (clickListenerRef.current) {
      g.maps.event.removeListener(clickListenerRef.current);
      clickListenerRef.current = null;
    }
    if (onMapClick) {
      clickListenerRef.current = g.maps.event.addListener(map, 'click', (ev) => {
        const ll = ev.latLng;
        if (!ll) return;
        onMapClick(ll.lat(), ll.lng());
      });
    }
  }, [ready, centerReady, resolvedCenter?.lat, resolvedCenter?.lng, center?.lat, center?.lng, zoom, showTraffic, showHeatMap, JSON.stringify(markers), JSON.stringify(heatMapPoints), JSON.stringify(route), onMapClick]);

  if (!hasKey) {
    return <Alert severity="warning">Google Maps key missing. Set VITE_GOOGLE_MAPS_API_KEY in frontend/.env.local</Alert>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!ready || !centerReady) {
    const skeletonHeight = typeof height === 'object' ? 320 : height;
    return <Skeleton variant="rounded" height={skeletonHeight} sx={{ borderRadius: 3 }} />;
  }

  return (
    <Box
      ref={mapRef}
      data-map-engine="google-maps"
      sx={{
        width: '100%',
        height,
        minHeight: height === '100%' ? 280 : undefined,
        borderRadius: height === '100%' ? 0 : 3,
        overflow: 'hidden',
      }}
    />
  );
}
